/*
 * Copyright Mimic Networks, Inc. 2024.
 */

import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { useTheme } from '@/hooks/useTheme';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

import MimicLogoStyles from './MimicLogo.module.css';
import MimicLogoSvg from './MimicLogo.svg?react';

export type MimicLogoProps = {
  width?: string;
  height?: string;
  color?: string;
};

export function MimicLogo({ width, height, color }: MimicLogoProps) {
  const theme = useTheme();
  const selectedTenantID = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const navigate = useNavigate();
  const userProfile = useMgmtPlaneStateStore((state) => state.userProfile);
  const clickCountRef = useRef(0);
  const clickTimerRef = useRef<NodeJS.Timeout | null>(null);

  const defaultColor = theme.tokens.color.components.logo.text;

  const handleLogoClick = (e: React.MouseEvent) => {
    if (userProfile?.role !== 'superadmin') {
      return;
    }

    if (e.metaKey || e.ctrlKey) {
      navigate(`/tenants/${selectedTenantID}/change-audit`);
      return;
    }

    clickCountRef.current += 1;

    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
    }

    clickTimerRef.current = setTimeout(() => {
      clickCountRef.current = 0;
    }, 1000);

    if (clickCountRef.current === 2) {
      clickCountRef.current = 0;
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
      }
      navigate(`/tenants/${selectedTenantID}/telemetry-viewer/admin`);
    }
  };

  return (
    <MimicLogoSvg
      style={{
        width,
        height,
        color: color || defaultColor,
      }}
      className={`${MimicLogoStyles.logo} anticon`}
      onClick={handleLogoClick}
    />
  );
}
